// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// Copyright (c) Utrecht University (Department of Information and Computing Sciences)

import * as types from "./Types";
import { store } from "@/store";
import json from "@/psdLang/lang.json";

/**
 * Get the userprefences from the store
 * @returns User preferences
 */
function getUserPreferences(): types.userPrefs {
  return store.getters.getPreferences;
}

/**
 * Get the userinfo from the store
 * @returns User info
 */
function getUserInfo(): types.userInfo | void {
  return store.getters.getUserInfo;
}

//Stores the available levels in the store
function storeLevels(levels: types.level[]): void {
  store.commit("clearLevels");
  store.commit("setLevels", levels);
}

// Stores the users stats in the store
function storeStats(data: types.stats): void {
  store.commit("setStats", data);
}

//Stores the currently loaded level in the store
function storeLevelInfo(currentLevel: types.levelInfo): void {
  store.commit("clearLevelInfo");
  store.commit("setLevelInfo", currentLevel);
}

//Stores the level settings in the store
function storeLevelSettings(currentLevel: types.levelSettings): void {
  store.commit("clearLevelSettings");
  store.commit("setLevelSettings", currentLevel);
}

function addExtra(value: string): void {
  if (window.location.pathname == "/docent/levelmaker") {
    const extraBlocks = store.getters.getLevelMakerExtraBlocks;
    const blocks = store.getters.getLevelMakerAnswerBlocks;

    if (!blocks.has(value)) {
      if (extraBlocks.has(value)) {
        extraBlocks.delete(value);
      } else {
        extraBlocks.add(value);
      }
    } else {
      if (extraBlocks.has(value)) {
        extraBlocks.delete(value);
      }
    }

    store.commit("setLevelMakerExtraBlocks", extraBlocks);
  }
}

function checkUsedBlock(value: string): boolean {
  if (!(window.location.pathname == "/docent/levelmaker")) return false;
  const check: boolean = isUsed(value) || isExtra(value);
  const remainingBlocks = store.getters.getLevelMakerRemainingBlocks;
  if (check) {
    if (remainingBlocks.has(value)) {
      remainingBlocks.delete(value);
    }
    store.commit("setLevelMakerRemainingBlocks", remainingBlocks);
  } else {
    if (!remainingBlocks.has(value)) {
      remainingBlocks.add(value);
    }
    store.commit("setLevelMakerRemainingBlocks", remainingBlocks);
  }
  return check;
}

function isUsed(value: string): boolean {
  const blocks = store.getters.getLevelMakerAnswerBlocks;
  return blocks.has(value);
}

function isExtra(value: string): boolean {
  const blocks = store.getters.getLevelMakerExtraBlocks;
  return blocks.has(value);
}

/**When completing a level, this function is capable of getting the level id of the next level, provided that the list of
 * levels stored in the store is still accurate and hasn't changed since the current level has loaded in.
 * @param levelId id of the current level
 * @returns returns information about the next level.
 */
function getNextLevel(levelId: number): types.level | undefined {
  if (store.getters.getLevels) {
    return store.getters.getLevels[
      store.getters.getLevels.findIndex(
        (obj: { levelId: number }) => obj.levelId == levelId
      ) + 1
    ];
  }
  return undefined;
}

/**
 * Gets all DIRECT children of the given HTMLElement with the given tag
 * @param elem the element whoese children you want to search
 * @param tagname the tag to filter on
 * @returns Boolean tagname equal
 */
function queryDirectChildrenOnly(
  elem: HTMLElement,
  tagname: string
): HTMLElement[] {
  if (!elem) return [];
  const childEls = elem.children;
  const newArr = Array.prototype.slice.call(childEls);
  const childDivs = newArr.filter(function (el) {
    return el.tagName === tagname;
  });

  return childDivs;
}

/**
 * Function that converts a full PSD tree to a JSON
 * @returns psdTree
 */
function createPSDTree(): string {
  const elemPSD = document.getElementById("psdBlock");
  const psdTree = subTree(
    queryDirectChildrenOnly(elemPSD as HTMLElement, "DIV")[0]
  );
  return JSON.stringify(psdTree);
}

function createPSDTreeFromString(input: string): string {
  const elemPSD = stringToHTML(input);
  const psdTree = subTree(
    queryDirectChildrenOnly(elemPSD as HTMLElement, "DIV")[0]
  );
  console.log("elemPSD", elemPSD);
  console.log("psdTree", psdTree);
  return JSON.stringify(psdTree);
}

function stringToHTML(str: string) {
  const dom = document.createElement("div");
  dom.innerHTML = str;
  return dom;
}

/**
 * Converts an HTML list of divs to a list of expressions in JSON format
 * Is used for the createPSDTree function
 * @param divElem HTML element containing the subtree
 * @returns An expression list in JSON format
 */
function subTree(divElem: HTMLElement): types.expression[] {
  const subElems: HTMLElement[] = queryDirectChildrenOnly(divElem, "DIV");
  const subArray: types.expression[] = [];

  for (let i = 0; i < subElems.length; i++) {
    subArray[i] = getNestedNode(subElems[i]);
  }
  //Remove all strange other elements used for styling
  subArray.filter(function (elem: types.expression) {
    return elem.type != "unknownExpression";
  });
  return subArray;
}

/**
 * Takes a HTMLElement representing a constant and converts
 * it to an expression for the parser.
 * @param varinput
 */
function makeConstantFromElement(
  varinput: HTMLElement
): types.constant | types.variable {
  //Checks if the dropdown menu is currently active and retrieves the value if it is.
  let constObj: types.constant | types.variable;
  //User input
  const inputField = queryDirectChildrenOnly(varinput, "INPUT")[0];
  if (!(inputField as HTMLInputElement).classList.contains("hidden"))
    if ((inputField as HTMLInputElement).type === "number")
      //Checks if its a number or a string.
      constObj = {
        type: "constant",
        const: {
          type: "int",
          value: Number.parseFloat((inputField as HTMLInputElement).value),
        },
      };
    else
      constObj = {
        type: "constant",
        const: {
          type: "string",
          value: (inputField as HTMLInputElement).value,
        },
      };
  //Dropdown for Enum. Enum are usually strings for clarity.
  else {
    const select = varinput.lastChild as HTMLSelectElement;
    const value = select.options[select.selectedIndex].value;
    //Checks if the Enum is specifically a boolean
    if (value === "true" || value === "false")
      constObj = {
        type: "constant",
        const: {
          type: "bool",
          value: value === "true",
        },
      };
    else
      constObj = {
        type: "constant",
        const: {
          type: "string",
          value: value,
        },
      };
  }
  const x = varinput.parentElement
    ?.querySelector(".lhs")
    ?.querySelector(".psdVar");
  if (x?.classList.contains("worldVar")) {
    const val = constObj.const.value as string;
    constObj = {
      type: "var",
      name: val,
    };
  }
  return constObj;
}

/**
 * Converts a HTML div element to an expression in JSON format. It converts one whole draggable block. It uses recursion for
 * nested blocks. Is used for the createPSDTree function
 * @param divElem an HTML element that represents a single PSD statement
 * @returns An expression in JSON format.
 */
function getNestedNode(divElem: HTMLElement): types.expression {
  if (divElem === null || divElem === undefined)
    return { type: "unknownExpression" };
  const className = divElem.className.split(" ")[0];
  const filling: HTMLElement[] = queryDirectChildrenOnly(divElem, "DIV");
  let parentDivs: HTMLElement[] = [];
  if (typeof filling[1] !== "undefined")
    parentDivs = queryDirectChildrenOnly(filling[1], "DIV");

  switch (className) {
    case "while": {
      const conditionDiv: HTMLElement[] = queryDirectChildrenOnly(
        parentDivs[0],
        "DIV"
      );
      const objWhile: types.whileLoop = {
        id: divElem.id,
        type: "while",
        condition: getNestedNode(
          queryDirectChildrenOnly(conditionDiv[0], "DIV")[0]
        ),
        body: subTree(parentDivs[1]),
      };
      return objWhile;
    }

    case "ifElse": {
      const conditionDiv: HTMLElement[] = queryDirectChildrenOnly(
        parentDivs[0],
        "DIV"
      );
      const branchDivs: HTMLElement[] = queryDirectChildrenOnly(
        parentDivs[2],
        "DIV"
      );
      const objIf: types.ifelseStatement = {
        id: divElem.id,
        type: "ifElse",
        condition: getNestedNode(
          queryDirectChildrenOnly(conditionDiv[0], "DIV")[0]
        ),
        ifCase: subTree(branchDivs[0]),
        elseCase: subTree(branchDivs[1]),
      };
      return objIf;
    }

    case "assign": {
      let rhs;
      //Checks if the dropdown menu is currently active and retrieves the value if it is.
      if (parentDivs[1].classList.contains("hidden")) {
        rhs = makeConstantFromElement(parentDivs[2]);
      } else
        rhs = getNestedNode(queryDirectChildrenOnly(parentDivs[1], "DIV")[0]);
      //Make sure an empty var doesnt cause a crash
      const lhs = getNestedNode(
        queryDirectChildrenOnly(parentDivs[0], "DIV")[0]
      );
      let varname = "";
      if (lhs.type === "var") varname = (lhs as types.variable).name;
      const objAssign: types.assignment = {
        id: divElem.id,
        type: "assignment",
        varName: varname,
        expression: rhs,
        usesExplicitVar: false,
      };
      return objAssign;
    }

    case "arithop":
    case "binop": {
      let rhs;
      //Checks if the dropdown menu is currently active and retrieves the value if it is.
      if (parentDivs[1].classList.contains("hidden")) {
        rhs = makeConstantFromElement(parentDivs[2]);
      } else
        rhs = getNestedNode(queryDirectChildrenOnly(parentDivs[1], "DIV")[0]);
      const x = divElem.lastChild as Element;
      const objBinop: types.binaryOp = {
        id: divElem.id,
        type: "binaryOp",
        op: [].slice
          .call(x.classList)
          .filter((word) =>
            [
              "multiply",
              "divide",
              "add",
              "subtract",
              "power",
              "mod",
              "eq",
              "neq",
              "lt",
              "gt",
              "lte",
              "gte",
              "and",
              "or",
              "xor",
            ].includes(word)
          )[0],
        lhs: getNestedNode(queryDirectChildrenOnly(parentDivs[0], "DIV")[0]),
        rhs: rhs,
      };
      return objBinop;
    }

    case "unop": {
      const x = divElem.lastChild as Element;
      const objUnary: types.unaryOp = {
        id: divElem.id,
        type: "unOp",
        op: [].slice
          .call((x.parentElement as HTMLElement).classList)
          .filter((word) => ["negate", "not"].includes(word))[0],
        expression: getNestedNode(
          queryDirectChildrenOnly(parentDivs[0], "DIV")[0]
        ),
      };
      return objUnary;
    }

    case "parop": {
      const objBracket: types.brackets = {
        id: divElem.id,
        type: "brackets",
        inside: getNestedNode(queryDirectChildrenOnly(parentDivs[0], "DIV")[0]),
      };
      return objBracket;
    }

    case "inop": {
      const objInf: types.constant = {
        id: divElem.id,
        type: "constant",
        const: {
          type: "bool",
          value: "true",
        },
      };
      return objInf;
    }

    case "variable": {
      let name = queryDirectChildrenOnly(filling[1], "P")[0].innerHTML;
      if (filling[1].classList.contains("worldVar")) {
        const lang = store.getters.getIDELanguage;
        name = (
          json as unknown as { [name: string]: { [name: string]: string } }
        )[lang][name];
      }
      const objVar: types.variable = {
        id: divElem.id,
        type: "var",
        name: name,
      };
      return objVar;
    }

    default: {
      const objFail: types.expression = {
        type: "unknownExpression",
      };
      return objFail;
    }
  }
}

export const exportedForTesting = {
  subTree,
  getNestedNode,
  makeConstantFromElement,
};

export {
  getUserPreferences,
  getUserInfo,
  storeLevels,
  storeStats,
  getNextLevel,
  storeLevelInfo,
  storeLevelSettings,
  queryDirectChildrenOnly,
  createPSDTree,
  createPSDTreeFromString,
  checkUsedBlock,
  addExtra,
};
